import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { editLyricTiming, LyricLineStartEdit, LyricTimingV1 } from 'fym-shared/src/music24/lyricTiming';
import { fymAPI, useGetLyricTimingQuery, usePatchLyricTimingMutation } from '../../../../../redux/api/fymAPI';
import type { StoreType } from '../../../../../redux/store';
import { useShowToast } from '../../../../../redux/slices/toasts/hooks';
import { useRealtimePollInterval } from '../../../../realtime/useRealtimePollInterval';
import { lyricTimingMessages } from './lyricTimingMessages';
import { lyricTimingRefusal } from './lyricTimingRefusal';

/** A change saves after this much time with no further change (design s8, spec §5.9). */
export const LYRIC_SAVE_DELAY_MS = 1000;

/**
 * While a sync runs, the editor reads the timing again every 5 s, but only while the realtime
 * socket is down. With the socket up, `catalog.song.updated` reads it again.
 */
const SYNCING_POLL_MS = 5000;

/** The new starts that the server does not have yet, by line index. Null clears a time. */
type PendingStarts = Record<number, number | null>;

function toEdits(pending: PendingStarts): LyricLineStartEdit[] {
  return Object.entries(pending).map(([index, startMs]) => ({ index: Number(index), startMs }));
}

/** The server timing with the pending starts on it, through the edit rule of the server. */
function applyPending(server: LyricTimingV1, pending: PendingStarts, durationMs: number) {
  return editLyricTiming(server, { revision: server.revision, lines: toEdits(pending) }, { durationMs });
}

interface UseLyricTimingEditorProps {
  accountId: number;
  itemUuid: string;
  /** The length of the master. Null when it is not known; then only the order rule applies. */
  durationSec: number | null;
  /** False skips the read: the song has no master or no lyrics. */
  enabled: boolean;
}

export interface LyricTimingEditor {
  /** The timing with the changes of this user on it (optimistic). Null: never synced. */
  timing: LyricTimingV1 | null | undefined;
  isLoading: boolean;
  error: ReturnType<typeof useGetLyricTimingQuery>['error'];
  retry: () => void;
  /** Sets the start of a line. False when the edit rule refuses it (the order); it then shows an error. */
  setStart: (index: number, startMs: number | null) => boolean;
  /** Moves the start of a timed line by `deltaMs`. A line with no time does not move. */
  nudge: (index: number, deltaMs: number) => boolean;
  /** True while a save runs or waits. */
  isSaving: boolean;
  /** Drops the changes that wait and their save timer (a re-sync drops the edits anyway). */
  discardPending: () => void;
}

/** The song length of the server when the song has no analysis (`LyricTiming.controller.ts`). */
const FALLBACK_DURATION_MS = 900_000;

/**
 * The lyric timing of one song and the edits of the sync editor (design s8, spec §5.9).
 *
 * - Each change applies at once to a local copy through `editLyricTiming`, the same rule as the
 *   server. A change that the rule refuses does not apply and shows the order error.
 * - The changes save together 1 s after the last change: `PATCH .../lyric-timing` with the revision
 *   of the server timing and the changed lines. The answer goes into the RTK cache.
 * - 409 `STALE` or `SYNCING`: the editor reads the timing again and drops the local changes.
 * - 400 `ORDER`: the editor drops the change of that line and shows the order error.
 * - When the sheet closes with a change that waits, the change saves at once.
 */
export function useLyricTimingEditor({
  accountId,
  itemUuid,
  durationSec,
  enabled,
}: UseLyricTimingEditorProps): LyricTimingEditor {
  const intl = useIntl();
  const dispatch = useDispatch<StoreType['dispatch']>();
  const { showError } = useShowToast();
  const args = useMemo(() => ({ accountId, itemUuid }), [accountId, itemUuid]);
  const query = useGetLyricTimingQuery(args, { skip: !enabled, refetchOnMountOrArgChange: true });
  const isSyncing = query.data?.timing?.status === 'syncing';
  // A second subscription that polls only while a sync runs and the socket is down.
  const pollingInterval = useRealtimePollInterval(SYNCING_POLL_MS);
  useGetLyricTimingQuery(args, { skip: !enabled || !isSyncing, pollingInterval });
  const [patchTiming] = usePatchLyricTimingMutation();

  const server = query.data?.timing;
  // The same length as the server: the analysis length, else 15 minutes.
  const durationMs = durationSec === null ? FALLBACK_DURATION_MS : Math.round(durationSec * 1000);

  const [pending, setPending] = useState<PendingStarts>({});
  const [isSending, setIsSending] = useState(false);
  // The timers and the save read the latest values through refs.
  const pendingRef = useRef<PendingStarts>({});
  const serverRef = useRef(server);
  serverRef.current = server;
  const durationRef = useRef(durationMs);
  durationRef.current = durationMs;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);

  const replacePending = useCallback((next: PendingStarts) => {
    pendingRef.current = next;
    setPending(next);
  }, []);

  const { refetch } = query;
  const readAgain = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  const scheduleSaveRef = useRef<() => void>(() => undefined);

  const save = useCallback(async (): Promise<void> => {
    const base = serverRef.current;
    const sent = pendingRef.current;
    if (inFlightRef.current || base === null || base === undefined || Object.keys(sent).length === 0) {
      return;
    }
    inFlightRef.current = true;
    setIsSending(true);
    try {
      const answer = await patchTiming({ ...args, revision: base.revision, lines: toEdits(sent) }).unwrap();
      dispatch(
        fymAPI.util.updateQueryData('getLyricTiming', args, (draft) => {
          draft.timing = answer.timing;
        })
      );
      // Keep only the changes made while the save ran.
      const rest: PendingStarts = {};
      Object.entries(pendingRef.current).forEach(([index, startMs]) => {
        if (sent[Number(index)] !== startMs) {
          rest[Number(index)] = startMs;
        }
      });
      replacePending(rest);
    } catch (error) {
      const refusal = lyricTimingRefusal(error);
      if (refusal.code === 'M24_LYRIC_TIMING_STALE' || refusal.code === 'M24_LYRIC_TIMING_SYNCING') {
        replacePending({});
        readAgain();
        showError(
          intl.formatMessage(
            refusal.code === 'M24_LYRIC_TIMING_STALE'
              ? lyricTimingMessages.staleError
              : lyricTimingMessages.syncingError
          )
        );
      } else if (refusal.code === 'M24_LYRIC_TIMING_ORDER') {
        // Roll back the line that breaks the order. With no index, roll back all sent lines.
        const rest = { ...pendingRef.current };
        if (refusal.index !== null && refusal.index in sent) {
          delete rest[refusal.index];
        } else {
          Object.keys(sent).forEach((index) => delete rest[Number(index)]);
        }
        replacePending(rest);
        showError(intl.formatMessage(lyricTimingMessages.orderError));
      } else {
        const rest = { ...pendingRef.current };
        Object.keys(sent).forEach((index) => {
          if (rest[Number(index)] === sent[Number(index)]) {
            delete rest[Number(index)];
          }
        });
        replacePending(rest);
        showError(intl.formatMessage(lyricTimingMessages.saveError));
      }
    } finally {
      inFlightRef.current = false;
      setIsSending(false);
      // A change made while the save ran saves in its turn.
      if (Object.keys(pendingRef.current).length > 0) {
        scheduleSaveRef.current();
      }
    }
  }, [args, dispatch, intl, patchTiming, readAgain, replacePending, showError]);

  const scheduleSave = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      save().catch(console.error);
    }, LYRIC_SAVE_DELAY_MS);
  }, [save]);
  scheduleSaveRef.current = scheduleSave;

  // The sheet closes: a change that waits saves at once.
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        saveRef.current().catch(console.error);
      }
    },
    []
  );

  const timing = useMemo(() => {
    if (server === null || server === undefined || Object.keys(pending).length === 0) {
      return server;
    }
    const result = applyPending(server, pending, durationMs);
    return result.ok ? result.timing : server;
  }, [durationMs, pending, server]);

  const setStart = useCallback(
    (index: number, startMs: number | null): boolean => {
      const base = serverRef.current;
      if (base === null || base === undefined || base.status === 'syncing') {
        return false;
      }
      const next = { ...pendingRef.current, [index]: startMs === null ? null : Math.max(0, Math.round(startMs)) };
      const result = applyPending(base, next, durationRef.current);
      if (!result.ok) {
        showError(intl.formatMessage(lyricTimingMessages.orderError));
        return false;
      }
      replacePending(next);
      scheduleSave();
      return true;
    },
    [intl, replacePending, scheduleSave, showError]
  );

  const nudge = useCallback(
    (index: number, deltaMs: number): boolean => {
      // Read the refs, not the render: two fast key presses both count.
      const start =
        index in pendingRef.current
          ? (pendingRef.current[index] ?? null)
          : (serverRef.current?.lines[index]?.startMs ?? null);
      if (start === null) {
        return false;
      }
      return setStart(index, Math.max(0, start + deltaMs));
    },
    [setStart]
  );

  const discardPending = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    replacePending({});
  }, [replacePending]);

  return {
    timing,
    isLoading: query.isLoading,
    error: query.error,
    retry: readAgain,
    setStart,
    nudge,
    isSaving: isSending || Object.keys(pending).length > 0,
    discardPending,
  };
}
